import { useEffect, useMemo, useState } from 'react';
import { Incident } from '../data/demoData';
import { ApiError, apiFetch } from '../lib/apiClient';
import { notifyIncidentExecutionUpdated } from '../lib/incidentExecutionEvents';

export interface IncidentAssignPayload {
  classification: string;
  classificationReason: string;
  classificationComment: string;
  assignee: string;
}

interface TenantUser {
  displayName: string;
  userPrincipalName: string;
  email?: string;
  role?: string;
  source?: string;
  canAssign?: boolean;
  lighthouseVerified?: boolean;
  autoAssign?: boolean;
}

interface Props {
  incident: Incident;
  customerId?: string;
  onCancel: () => void;
  onUpdated: (payload: IncidentAssignPayload) => void;
}

// Same backend the Incidents list's inline "Assign" control uses:
// GET /incidents/assignees for the SOC roster, POST /incidents/:id/assign
// to apply it — this dialog just gives the incident detail view (which has
// no table row to host an inline dropdown in) the same capability.
export function IncidentAssignDialog({ incident, customerId, onCancel, onUpdated }: Props) {
  const [tenantUsers, setTenantUsers] = useState<TenantUser[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [filterText, setFilterText] = useState('');
  const [selectedValue, setSelectedValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fallbackPrompt, setFallbackPrompt] = useState<string | null>(null);
  const [fallbackAllowed, setFallbackAllowed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setUsersLoading(true);
    const qs = customerId ? `?customerId=${encodeURIComponent(customerId)}` : '';
    apiFetch<{ ok: boolean; assignees: TenantUser[] }>(`/incidents/assignees${qs}`).then((response) => {
      if (!cancelled) setTenantUsers(Array.isArray(response.assignees) ? response.assignees : []);
    }).catch(() => { if (!cancelled) setTenantUsers([]); }).finally(() => { if (!cancelled) setUsersLoading(false); });
    return () => { cancelled = true; };
  }, [customerId]);

  const filteredUsers = useMemo(() => tenantUsers.filter(u => {
    if (!filterText) return true;
    const q = filterText.toLowerCase();
    return u.displayName.toLowerCase().includes(q) || u.userPrincipalName.toLowerCase().includes(q) || String(u.role ?? '').toLowerCase().includes(q) || String(u.email ?? '').toLowerCase().includes(q);
  }), [tenantUsers, filterText]);

  function selectUser(user: TenantUser) {
    setSelectedValue(user.userPrincipalName || user.email || '');
    setFilterText(user.displayName);
  }

  async function submitAssign(useServicePrincipalFallback = false) {
    const trimmed = selectedValue.trim() || filterText.trim();
    if (!trimmed) { setError('Select an analyst'); return; }
    setLoading(true);
    setError(null);
    if (!useServicePrincipalFallback) { setFallbackPrompt(null); setFallbackAllowed(false); }
    try {
      const qs = customerId ? `?customerId=${encodeURIComponent(customerId)}` : '';
      const response = await apiFetch(`/incidents/${encodeURIComponent(incident.id)}/assign${qs}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          assignee: trimmed,
          armIncidentName: incident.armIncidentName,
          graphIncidentId: incident.graphIncidentId,
          useServicePrincipalFallback,
        }),
      });
      void response;
      notifyIncidentExecutionUpdated(incident.id);
      onUpdated({ classification: '', classificationReason: '', classificationComment: '', assignee: trimmed });
    } catch (e) {
      if (e instanceof ApiError && e.status === 409 && e.body?.['error'] === 'delegated_rbac_required') {
        setFallbackPrompt(typeof e.body['message'] === 'string' ? e.body['message'] as string : 'Your user identity could not assign this incident.');
        setFallbackAllowed(e.body?.['canUseServicePrincipalFallback'] === true);
        setError(null);
      } else {
        setError(e instanceof Error ? e.message : String(e));
      }
      setLoading(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center px-4"
      onClick={onCancel}
    >
      <div
        className="bg-card rounded-xl shadow-2xl w-full max-w-md"
        onClick={e => e.stopPropagation()}
      >
        <div className="p-6">
          <div className="mb-5">
            <h2 className="text-base font-bold text-foreground mb-1">Assign Incident</h2>
            <p className="text-xs text-muted-foreground font-mono">
              #{incident.incidentNumber} · {incident.title.length > 60 ? incident.title.slice(0, 60) + '…' : incident.title}
            </p>
            {incident.assignedTo && (
              <p className="text-[11px] text-muted-foreground mt-1">
                Currently assigned: <span className="font-semibold">{incident.assignedTo}</span>
              </p>
            )}
          </div>

          <div className="mb-5 relative">
            <label className="block text-xs font-semibold text-foreground mb-2">
              SOC analyst <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={filterText}
              onChange={e => { setFilterText(e.target.value); setSelectedValue(''); }}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); void submitAssign(); } }}
              placeholder={usersLoading ? 'Loading…' : tenantUsers.length === 0 ? 'No eligible SOC assignees' : 'Search SOC analyst…'}
              disabled={loading}
              autoFocus
              className="w-full rounded-lg border border-border bg-background text-sm text-foreground px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary/50 placeholder:text-muted-foreground/60"
            />
            {!selectedValue && (
              <div className="mt-1 max-h-52 overflow-y-auto rounded-lg border border-border bg-card shadow-lg">
                {usersLoading && <div className="px-3 py-2 text-xs italic text-muted-foreground">Loading users…</div>}
                {!usersLoading && tenantUsers.length === 0 && (
                  <div className="px-3 py-2 text-xs italic text-muted-foreground">
                    No eligible SOC assignees. Check Masters → SOC Assignment Master, Users Master customer access, and incidents.assign permission.
                  </div>
                )}
                {!usersLoading && filteredUsers.slice(0, 25).map(user => (
                  <button
                    key={user.userPrincipalName}
                    type="button"
                    onMouseDown={e => { e.preventDefault(); selectUser(user); }}
                    className="w-full text-left px-3 py-2 text-xs hover:bg-muted transition-colors flex flex-col"
                  >
                    <span className="font-medium text-foreground">{user.displayName}</span>
                    <span className="text-muted-foreground text-[10px] truncate">{user.userPrincipalName}</span>
                    <span className="text-muted-foreground text-[10px] truncate">{user.role ? `${user.role}` : 'SOC roster'}{user.lighthouseVerified ? ' · Lighthouse verified' : ''}{user.autoAssign ? ' · Auto-assign ready' : ''}</span>
                  </button>
                ))}
                {!usersLoading && filteredUsers.length > 25 && (
                  <div className="px-3 py-1.5 text-[10px] text-muted-foreground border-t border-border bg-muted/30">
                    Showing 25 of {filteredUsers.length} eligible SOC assignees. Type to filter.
                  </div>
                )}
                {!usersLoading && tenantUsers.length > 0 && filteredUsers.length === 0 && (
                  <div className="px-3 py-2 text-xs italic text-muted-foreground">No matches for "{filterText}"</div>
                )}
              </div>
            )}
          </div>

          {fallbackPrompt && (
            <div className="mb-4 rounded-lg border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/40 p-3">
              <div className="text-xs font-semibold text-amber-900 dark:text-amber-200 mb-1">Delegated user action failed</div>
              <div className="text-xs text-amber-800 dark:text-amber-100 mb-3">{fallbackPrompt}</div>
              <div className="flex gap-2">
                {fallbackAllowed && (
                  <button type="button" onClick={() => void submitAssign(true)} disabled={loading} className="px-3 py-1.5 rounded bg-amber-600 hover:bg-amber-700 text-white text-xs font-semibold disabled:opacity-50">
                    Yes, use Service Principal
                  </button>
                )}
                <button type="button" onClick={() => { setFallbackPrompt(null); setFallbackAllowed(false); }} disabled={loading} className="px-3 py-1.5 rounded border border-amber-300 dark:border-amber-700 text-amber-900 dark:text-amber-100 text-xs font-semibold disabled:opacity-50">
                  No, cancel
                </button>
              </div>
            </div>
          )}

          {error && (
            <div className="mb-4 px-3 py-2 rounded-lg bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 text-xs text-red-700 dark:text-red-300">
              {error}
            </div>
          )}

          <div className="flex gap-3">
            <button
              onClick={onCancel}
              disabled={loading}
              className="flex-1 py-2.5 rounded-lg border border-border text-sm font-semibold text-foreground hover:bg-muted transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={() => void submitAssign()}
              disabled={loading || !(selectedValue.trim() || filterText.trim())}
              className="flex-1 py-2.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin inline-block" />
                  Assigning…
                </>
              ) : 'Assign Incident'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
